<template>
    <div class="vx-graph">
        <vue-apex-charts
            class="w-full"
            ref="chart"
            :type="type"
            :key="chartKey"
            :height="height"
            :options="chartOptions"
            :series="localSeries"
        />
    </div>
</template>

<script>
import chartTypeInfo from './reference/chartTypes.js'
import { eventNames } from './reference/chartEvents.js'
import _color from '@/assets/utils/color.js'
import Vue from 'vue'
import chartMixins from './chartMixins.js'

export default {
    name: 'vx-graph',
    mixins: [chartMixins],
    props: {
        value: {
            type: null
        },
        config: {
            type: Object,
            default: () => {
                return {}
            }
        },
        type: {
            type: String,
            required: true,
            default: 'bar',
            validator: (v) => Object.keys(chartTypeInfo).contains(v)
        },
        height: {
            type: Number,
            default: 300
        },
        fullInterface: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: 'primary'
        },
        colorKey: {
            type: [Function, String]
        },
        monochrome: {
            type: Boolean,
            default: false
        },
        showTooltip: {
            type: Boolean,
            default: false
        },
        showDataLabels: {
            type: Boolean,
            default: false
        },
        valueFormat: {
            type: [Function, String],
            default: 'number'
        }
    },
    watch: {
        fullInterface: {
            handler: function (newV, oldV) {
                this.chartKey++
            }
        },
        type: {
            handler: function (newV, oldV) {
                this.chartKey++
            }
        },
        value: {
            handler: function (newV, oldV) {
                var newId = this.getIdForDataPoint(newV)
                var oldId = this.getIdForDataPoint(oldV)

                if (newId === oldId) {
                    return
                }

                var seriesIndex = null
                var dataPointIndex = null

                this.series.first((series, index) => {
                    dataPointIndex = series.data.findIndex(
                        (dataPoint) => this.getIdForDataPoint(dataPoint) === newId
                    )
                    if (dataPointIndex !== -1) {
                        seriesIndex = index
                        return true
                    }
                    dataPointIndex = null
                })

                this.selectDataPointForIndexes({
                    seriesIndex,
                    dataPointIndex
                })
            }
        }
    },
    data() {
        return {
            localValue: null,
            chartContext: null,
            chartKey: 1,
            emptySeries: [
                {
                    name: 'Empty',
                    data: [
                        {
                            name: 'Empty',
                            value: 0
                        }
                    ]
                }
            ]
        }
    },
    computed: {
        listeners() {
            var listeners = {
                ...this.$listeners,
                mounted: this.onChartMounted,
                dataPointSelection: this.onDataPointSelection
            }
            delete listeners.input
            return listeners
        },
        selectedSeriesIndex() {
            if (!this.localValue) {
                return null
            }
            return this.localValue.seriesIndex
        },
        selectedDataPointIndex() {
            if (!this.localValue) {
                return null
            }
            return this.localValue.dataPointIndex
        },
        hasValue() {
            return this.selectedDataPointIndex != null && this.selectedSeriesIndex != null
        },
        selectedDataPoint() {
            if (!this.hasValue) {
                return null
            }
            return this.series[this.selectedSeriesIndex].data[this.selectedDataPointIndex]
        },
        chartType() {
            return chartTypeInfo[this.type]
        },
        getCustomColor() {
            if (!this.colorKey) {
                return null
            }
            if (typeof this.colorKey === 'function') {
                return this.colorKey
            }
            return (v) => v[this.colorKey]
        },
        chartOptions() {
            return Object.deepMerge(this.defaultOptions, this.config || {})
        },
        defaultOptions() {
            return {
                chart: {
                    width: '100%',
                    height: this.height,
                    events: this.events,
                    type: this.type,
                    toolbar: {
                        show: this.fullInterface
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 300
                    }
                },
                colors: this.colors,
                tooltip: this.tooltip,
                labels: this.allLabels,
                grid: {
                    show: false,
                    borderColor: '#e0e0e0'
                },
                dataLabels: {
                    enabled: this.showDataLabels,
                    formatter: this.formatValue
                },
                noData: {
                    text: 'No Data'
                },
                states: {
                    active: {
                        filter: {
                            type: this.themeMode + 'en',
                            value: this.themeMode == 'light' ? 0.2 : 0.5
                        }
                    }
                },
                legend: {
                    fontFamily: 'inherit',
                    markers: {
                        width: 12,
                        height: 12,
                        radius: 12,
                        offsetX: -4,
                        offsetY: 0
                    }
                }
            }
        },
        events() {
            var eventHandlers = eventNames.toObject(
                (eventName) => eventName,
                (eventName) =>
                    (...args) => {
                        this.$emit(eventName, ...args)
                    }
            )

            eventHandlers.mounted = this.onChartMounted
            eventHandlers.dataPointSelection = this.onDataPointSelection

            return eventHandlers
        },
        tooltip() {
            var tooltip = {
                enabled: this.showTooltip,
                shared: !this.isSingleSeries,
                intersect: this.isSingleSeries,
                theme: this.themeMode,
                y: {
                    formatter: this.formatValue
                },
                onDatasetHover: {
                    highlightDataSeries: true
                },
                followCursor: true
            }

            if (this.$scopedSlots.tooltip) {
                tooltip.custom = function ({ series, seriesIndex, dataPointIndex, w }) {
                    var dataPoint = this.series[seriesIndex].data[dataPointIndex]
                    // Render the slot content
                    const vnode = this.$scopedSlots.tooltip({
                        dataPoint,
                        series,
                        seriesIndex,
                        dataPointIndex,
                        w
                    })

                    // Create a temporary Vue instance to mount the vnode
                    const vm = new Vue({
                        render: (h) => vnode
                    }).$mount()

                    // Get the HTML of the component
                    const html = vm.$el.outerHTML

                    return html
                }.bind(this)
            }
            return tooltip
        },
        allLabels() {
            return this.allDataPoints.distinct((e) => e.name)
        },
        baseColor() {
            return _color.getHexColor(this.color)
        },
        colors() {
            var coloredItems = this.allDataPoints
            if (this.isColorPerSeries) {
                coloredItems = this.series
            }

            if (this.colorKey) {
                return coloredItems.map(this.getCustomColor)
            }

            if (this.monochrome) {
                return _color.getMonochromaticSet(this.baseColor, coloredItems.length)
            }

            if (this.isColorPerSeries) {
                // if colors are per series, every color MUST be different
                return _color.getAlgorithmicSet(this.baseColor, coloredItems.length)
            }

            return _color.getAlgorithmicSet(this.baseColor, coloredItems.length)
        },
        isCartesianSeries() {
            return this.chartType.seriesType.toLowerCase().contains('cartesian')
        },
        isColorPerSeries() {
            return this.isCartesianSeries
        },
        localSeries() {
            var series = this.series
            if (this.noSeries) {
                if (this.type == 'pie' || this.type == 'donut') {
                    return []
                }
                series = this.emptySeries
            }

            if (!this.isCartesianSeries) {
                return series.first().data.map((dataPoint) => dataPoint.value)
            }

            return series.map((singleSeries, seriesIndex) => {
                var seriesName = singleSeries.name
                var seriesDataMap = singleSeries.data.toObject((d) => d.name)

                var data = this.allLabels.map((label) => {
                    var dataPoint = seriesDataMap[label]
                    if (!dataPoint) {
                        return {
                            x: label,
                            y: 0,
                            goals: []
                        }
                    }

                    var goals = []
                    if (dataPoint.goal) {
                        goals = [
                            {
                                name: 'Goal',
                                value: dataPoint.goal,
                                strokeColor: _color.getComplement(this.baseColor)
                            }
                        ]
                    }

                    return {
                        x: label,
                        y: dataPoint.value,
                        goals
                    }
                })

                return {
                    name: seriesName,
                    type: singleSeries.type,
                    data
                }
            })
        },
        formatValue() {
            if (typeof this.valueFormat === 'function') {
                return this.valueFormat
            }

            switch (this.valueFormat.toLowerCase()) {
                case 'currency':
                    return (v) => this.formatCurrency(v)
                case 'percentage':
                    return (v) => this.formatPercentage(v)
                default:
                    return (v) => this.formatNumber(v)
            }
        }
    },
    methods: {
        getIdForDataPoint(dataPoint) {
            if (!dataPoint) {
                return null
            }
            return `${dataPoint.name}_${dataPoint.value}`
        },
        getDataPointById(id) {
            return this.allDataPoints.first((dataPoint) => this.getIdForDataPoint(dataPoint) === id)
        },
        formatCurrency(currency) {
            if (currency < 1000) {
                return `$${(currency || 0).formatCurrency()}`
            }
            return `$${this.formatNumber(currency)}`
        },
        formatPercentage(percentage) {
            return `${(percentage || 0).round(0)}%`
        },
        formatNumber(number) {
            return (number || 0).abbreviate()
        },
        onDataPointSelection(event, chartContext, config) {
            var { seriesIndex, dataPointIndex } = config
            var newValue = {
                seriesIndex: config.seriesIndex,
                dataPointIndex: config.dataPointIndex
            }

            if (
                seriesIndex == this.selectedSeriesIndex &&
                dataPointIndex == this.selectedDataPointIndex
            ) {
                newValue = {
                    seriesIndex: null,
                    dataPointIndex: null
                }
            }

            this.localValue = newValue

            if (event) {
                this.$emit('input', this.selectedDataPoint)
            }

            this.$emit('dataPointSelection', event, chartContext, config)
        },
        onChartMounted(context, config) {
            if (!context.w.globals.selectedDataPoints.any()) {
                context.w.globals.selectedDataPoints.push([])
            }
            this.chartContext = context
            this.$emit('beforeChartMounted', context, config)
        },
        clearSelections() {
            this.chartContext.w.globals.selectedDataPoints.forEach(
                (seriesSelections, seriesIndex) => {
                    seriesSelections.forEach((dataPointIndex) => {
                        this.toggleDataPoint(seriesIndex, dataPointIndex)
                    })
                }
            )
        },
        selectDataPointForIndexes({ seriesIndex, dataPointIndex }) {
            if (!this.chartContext) {
                return
            }

            if (dataPointIndex == null || seriesIndex == null) {
                this.clearSelections()
                return
            }

            if (
                seriesIndex == this.selectedSeriesIndex &&
                dataPointIndex == this.selectedDataPointIndex
            ) {
                return
            }
            this.toggleDataPoint(seriesIndex, dataPointIndex)
        },
        toggleDataPoint(seriesIndex, dataPointIndex) {
            if (!this.chartType.isMultiEnabled) {
                this.chartContext.toggleDataPointSelection(dataPointIndex)
                return
            }

            this.chartContext.toggleDataPointSelection(seriesIndex, dataPointIndex)
        }
    }
}
</script>

<style lang="scss" scoped>
.vx-graph {
    ::v-deep {
        .apexcharts-series {
            cursor: pointer;
        }
        .apexcharts-svg {
            overflow: visible;
        }
    }
}
</style>
